import { useFocusEffect } from '@react-navigation/native'
import { platform } from 'constants'
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'

import { Box } from 'widgets/Box'
import { TextButton } from 'widgets/Button'
import { Pressable } from 'widgets/Pressable'

import { AllowedFileType } from 'types/file'

import styles from './styles'
import { FileSizeErrorModal } from './subcomponents/FileSizeErrorModal'

const fileTypeAcceptStringMap: { [key in AllowedFileType]: string } = {
  image: 'image/*',
  video: 'video/*',
  both: 'image/*, video/*',
}

interface Props {
  fileType: AllowedFileType
  onFileSelect: ((file: React.ChangeEvent<HTMLInputElement>) => void) | ((file: string) => void)
  multiple?: boolean
  title?: string
}

export const UploadButton = forwardRef(
  ({ fileType, onFileSelect, multiple = false, title }: Props, ref: any): JSX.Element => {
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
    const fileUploadRef = useRef<HTMLInputElement>(null)
    const openFileUpload = () => fileUploadRef.current?.click()

    useImperativeHandle(ref, () => ({
      openFileUpload: () => {
        openFileUpload()
      },
    }))

    const openErrorModal = () => {
      setIsErrorModalOpen(true)
    }

    const closeErrorModal = () => {
      setIsErrorModalOpen(false)
    }

    // Manually clear the selected file when navigating away from this page
    useFocusEffect(() => () => {
      if (fileUploadRef?.current?.value) {
        fileUploadRef.current.value = ''
      }
    })

    const handleFileSelect = (fileSelectedEvent: React.ChangeEvent<HTMLInputElement>) => {
      try {
        // @ts-expect-error - This should work, its just typescript complaining about the native flow
        onFileSelect(fileSelectedEvent)
      } catch (error) {
        openErrorModal()
      }
    }

    if (!platform.IS_WEB) return <></>

    return (
      <Box style={styles.pressable}>
        <FileSizeErrorModal isVisible={isErrorModalOpen} onClose={closeErrorModal} />
        <input
          hidden
          multiple={multiple}
          ref={fileUploadRef}
          type='file'
          accept={fileTypeAcceptStringMap[fileType]}
          onChange={handleFileSelect}
        />

        {title ? (
          <TextButton
            testID='UploadTextButton'
            type='black'
            icon='upload'
            onPress={openFileUpload}
            title={title}
          />
        ) : (
          <Pressable
            testID='UploadButtonPressable'
            onPress={openFileUpload}
            style={styles.pressable}
          />
        )}
      </Box>
    )
  },
)
